'use client';

import clsx from 'clsx';

import { CardContent, CardHeader } from '@/modules/players/v3/components/shared/card/card';
import {
  DashboardEmptyMascotLayer,
  type EmptyMascotSize,
} from '@/modules/players/v3/components/shared/dashboard-widget-card/dashboard-empty-mascot-layer';
import {
  PlayerDashboardEmptyContent,
  type PlayerDashboardEmptyContentProps,
} from '@/modules/players/v3/components/shared/dashboard-widget-card/player-dashboard-empty-content';
import { CardContext } from '@/modules/players/v3/contexts/card-context';
import { useIsMobileCard } from '@/modules/players/v3/hooks/use-is-mobile-card';
import { toWidgetErrorState } from '@/modules/players/v3/utils/widget-error-state';

export interface DashboardWidgetCardProps {
  header: React.ReactNode;
  footer?: React.ReactElement;
  isLoading?: boolean;
  isError?: boolean;
  isEmpty?: boolean;
  skeleton?: React.ReactNode;
  emptyState?: PlayerDashboardEmptyContentProps;
  /** With `isError`, shows `emptyState`'s illustration with this title and a Try again button. */
  errorState?: { title: string; onRetry: () => void };
  contentClassName?: string;
  className?: string;
  emptyClassName?: string;
  emptyMascotSize?: EmptyMascotSize;
  children: React.ReactNode;
}

const CardShell = ({
  children,
  footer,
  className,
  ...props
}: React.ComponentProps<'div'> & { footer?: React.ReactElement }) => {
  const isMobile = useIsMobileCard();

  return (
    <CardContext.Provider value={{ isMobile, isWidget: true }}>
      <div
        className={clsx(
          'group relative flex size-full flex-col items-stretch justify-stretch overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-[box-shadow,border-color] duration-200 ease-out md:hover:border-gray-300 md:hover:shadow-md motion-reduce:transition-none motion-reduce:md:hover:border-gray-200 motion-reduce:md:hover:shadow-sm',
          className
        )}
        {...props}
      >
        {children}
        {footer ?? <div />}
      </div>
    </CardContext.Provider>
  );
};

export const DashboardWidgetCard = ({
  header,
  footer,
  isLoading,
  isError,
  isEmpty,
  skeleton,
  emptyState: emptyStateProp,
  errorState,
  contentClassName,
  className,
  emptyClassName,
  emptyMascotSize = 'default',
  children,
}: DashboardWidgetCardProps) => {
  if (isLoading && skeleton) {
    return <div className='h-full w-full'>{skeleton}</div>;
  }

  const emptyState =
    isError && errorState && emptyStateProp
      ? toWidgetErrorState(emptyStateProp, errorState.title, errorState.onRetry)
      : emptyStateProp;
  const showEmpty = (isError || isEmpty) && !!emptyState;

  return (
    <CardShell
      footer={showEmpty ? undefined : footer}
      className={clsx(
        'h-full',
        className,
        showEmpty && '!pb-0 min-h-[300px] md:min-h-0',
        showEmpty && emptyClassName
      )}
    >
      <CardHeader>{header}</CardHeader>
      {showEmpty ? (
        <>
          <div className='relative z-[1] flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'>
            <PlayerDashboardEmptyContent
              {...emptyState!}
              className='h-full min-h-0'
              mascotInCardShell={emptyState!.visual.kind === 'mascot'}
              mascotSize={emptyMascotSize}
            />
          </div>
          {emptyState!.visual.kind === 'mascot' ? (
            <DashboardEmptyMascotLayer
              src={emptyState!.visual.src}
              position={emptyState!.visual.position}
              size={emptyMascotSize}
              slotClassName={emptyState!.mascotSlotClassName}
              className={emptyState!.visual.className}
              width={emptyState!.visual.width}
              height={emptyState!.visual.height}
            />
          ) : null}
        </>
      ) : (
        <CardContent className={clsx('flex-1 min-h-0', contentClassName)}>{children}</CardContent>
      )}
    </CardShell>
  );
};
